import { useWelcomeTourGuide } from "@app/components/assistant/WelcomeTourGuideProvider";
import { getAdminSectionHref } from "@app/components/navigation/config";
import type { SidebarNavigation } from "@app/components/navigation/config";
import { NavigationSidebar } from "@app/components/navigation/NavigationSidebar";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { useAuth } from "@app/lib/auth/AuthContext";
import { useAppRouter } from "@app/lib/platform";
import { useWorkspacePermissions } from "@app/lib/swr/permissions";
import { useSpaces, useSystemSpace } from "@app/lib/swr/spaces";
import { TRACKING_AREAS, trackEvent } from "@app/lib/tracking";
import { hasGroupManagementScope } from "@app/types/api/auth_context";
import type { SubscriptionType } from "@app/types/plan";
import type { WorkspaceType } from "@app/types/user";
import {
  AzimutAgent,
  AzimutCollaboration,
  AzimutConnections,
  AzimutConversation,
  AzimutData,
  AzimutModuleRail,
  AzimutRegion,
  AzimutSettings,
  Button,
  LayoutLeft,
  Menu01,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@dust-tt/sparkle";
import type { AzimutNavigationItem } from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import type React from "react";
import { useContext, useRef } from "react";

interface NavigationProps {
  hideSidebar: boolean;
  owner: WorkspaceType;
  subscription: SubscriptionType;
  navChildren?: React.ReactNode;
  subNavigation?: SidebarNavigation[] | null;
  isNavigationBarOpen: boolean;
  setNavigationBarOpen: (isOpen: boolean) => void;
  isFullScreen: boolean;
  isMobile: boolean;
}

export function Navigation({
  hideSidebar,
  owner,
  subscription,
  navChildren,
  subNavigation,
  isNavigationBarOpen,
  setNavigationBarOpen,
  isFullScreen,
  isMobile,
}: NavigationProps) {
  const { t } = useLingui();
  const router = useAppRouter();
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
  const { isAdmin, featureFlags, groupManagement } = useAuth();
  const { hasPermission } = useWorkspacePermissions();
  const { spaceMenuButtonRef } = useWelcomeTourGuide();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { spaces } = useSpaces({
    workspaceId: owner.sId,
    kinds: ["global", "regular"],
  });
  const { systemSpace } = useSystemSpace({
    workspaceId: owner.sId,
    disabled: !isAdmin,
  });
  const dataSpace =
    spaces.find((space) => space.kind === "global") ?? spaces[0];
  const adminHref = getAdminSectionHref(
    owner,
    hasPermission,
    featureFlags.includes("group_management") &&
      hasGroupManagementScope(groupManagement?.read_usage)
  );
  const path = router.asPath.split(/[?#]/)[0];
  const prefix = `/w/${owner.sId}`;
  const isConnection = isAdmin && path.includes("/categories/managed");
  const isData =
    path.includes("/categories/folder") ||
    (!isAdmin && path.includes("/categories/managed"));
  const items: AzimutNavigationItem[] = [
    {
      id: "conversations",
      label: t`Conversations`,
      icon: AzimutConversation,
      href: `${prefix}/conversation/new`,
      selected:
        path.includes("/conversation/") || path.endsWith("/get-started"),
    },
    {
      id: "collaboration",
      label: t`Collaborative work`,
      icon: AzimutCollaboration,
      href: `${prefix}/pods`,
      selected: path.includes("/pods"),
    },
    {
      id: "agents",
      label: t`Agents`,
      icon: AzimutAgent,
      href: `${prefix}/builder/agents`,
      selected: path.includes("/builder/"),
    },
    {
      id: "regions",
      label: t`Regions`,
      icon: AzimutRegion,
      href: `${prefix}/spaces`,
      selected: path.includes("/spaces") && !isConnection && !isData,
      containerRef: spaceMenuButtonRef,
    },
    {
      id: "data",
      label: t`Data sources`,
      icon: AzimutData,
      href: dataSpace
        ? `${prefix}/spaces/${dataSpace.sId}/categories/folder`
        : undefined,
      disabled: !dataSpace,
      selected: isData,
    },
    ...(isAdmin
      ? [
          {
            id: "connections",
            label: t`Connections`,
            icon: AzimutConnections,
            href: systemSpace
              ? `${prefix}/spaces/${systemSpace.sId}/categories/managed`
              : undefined,
            disabled: !systemSpace,
            selected: isConnection,
          },
        ]
      : []),
    ...(adminHref
      ? [
          {
            id: "settings",
            label: t`Settings`,
            icon: AzimutSettings,
            href: adminHref,
            selected: !!subNavigation?.some(
              (nav) =>
                nav.id === "organization" ||
                nav.id === "platform" ||
                nav.id === "spend"
            ),
          },
        ]
      : []),
  ];
  if (hideSidebar || isFullScreen) {
    return null;
  }
  const rail = (
    <AzimutModuleRail
      label={t`Workspace navigation`}
      items={items}
      onNavigate={(event, item) => {
        if (
          !item.href ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        trackEvent({
          area: TRACKING_AREAS.NAVIGATION,
          object: "workspace_module",
          extra: { module: item.id },
        });
        setSidebarOpen(false);
        void router.push(item.href);
      }}
      footer={
        !isMobile && (
          <button
            type="button"
            ref={toggleRef}
            className="az-context-toggle"
            aria-label={
              isNavigationBarOpen
                ? t`Hide context navigation`
                : t`Show context navigation`
            }
            aria-expanded={isNavigationBarOpen}
            aria-controls="azimut-context"
            onClick={() => {
              trackEvent({
                area: TRACKING_AREAS.NAVIGATION,
                object: "context_navigation_toggle",
              });
              setNavigationBarOpen(!isNavigationBarOpen);
            }}
          >
            <LayoutLeft className="h-5 w-5" />
          </button>
        )
      }
    />
  );
  const context = (
    <NavigationSidebar
      owner={owner}
      subscription={subscription}
      subNavigation={subNavigation}
      isMobile={isMobile}
      onHideContext={() => {
        if (isMobile) {
          setSidebarOpen(false);
        } else {
          setNavigationBarOpen(false);
          toggleRef.current?.focus();
        }
      }}
    >
      {navChildren}
    </NavigationSidebar>
  );
  return (
    <div className="az-navigation">
      {isMobile ? (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <div className="fixed left-2 top-[calc(var(--banner-height)+var(--az-workspace-bar-height)+0.5rem)] z-40">
            <SheetTrigger asChild>
              <Button
                variant="outline"
                icon={Menu01}
                tooltip={t`Open navigation`}
                aria-label={t`Open navigation`}
                onClick={() => setSidebarOpen(true)}
              />
            </SheetTrigger>
          </div>
          <SheetContent
            side="left"
            className="flex w-full max-w-[26rem] bg-panel-background p-0"
          >
            <SheetHeader className="sr-only" hideButton>
              <SheetTitle>{t`Workspace navigation`}</SheetTitle>
            </SheetHeader>
            <div className="az-navigation min-h-0 w-full flex-1">
              {rail}
              <div className="az-context-panel">{context}</div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <>
          {rail}
          <aside
            id="azimut-context"
            className="az-context-panel"
            data-collapsed={!isNavigationBarOpen}
          >
            {context}
          </aside>
        </>
      )}
    </div>
  );
}
